import { isSameDay, format, getDate } from 'date-fns'
import {
  bool,
  func,
  instanceOf,
  number,
  object,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types'
import { useCallback } from 'react'
import { renderBreakdownTooltipContent } from '@opallabs/calendar'
import { Timescale } from '@opallabs/concepts'
import { kuiperClassNames } from '@opallabs/kuiper'
import { Tooltip } from '@opallabs/lapidary'
import { formatDayHeading, HistogramBar } from '../../HistogramBar'
import { useHistogramDayDropHandlers } from '../../useHistogramDayDropHandlers'
import DropWarner from '../DropWarner'

import '../../Day/styles.scss'

const TODAY = new Date()

const formatDateString = (date) => format(date, 'yyyy-MM-dd')

const Day = ({
  date,
  dayHeight,
  daySize,
  breakdown,
  isContentMode,
  draggedMomentParams,
  isFirstDayOfWeek,
  isLastDayOfWeek,
  isTooltipFastTrack,
  mode,
  onBarHoverChange,
  onClick,
  onRescheduleContent,
  onUpdateMoment,
  story,
}) => {
  const {
    isActiveDropTarget,
    isForbiddenDropTarget,
    isForbiddenMomentDrop,
    isNotWithinStory,
    isSameDateAsDragged,
    onDragEnter,
    onDragLeave,
    onDragOver,
    onDrop,
  } = useHistogramDayDropHandlers({
    date,
    story,
    draggedMoment: draggedMomentParams,
    onRescheduleContent,
    onUpdateMoment,
    formatUpdateDate: formatDateString,
    activateOnDragOver: true,
  })

  const onClickDay = useCallback(() => {
    if (mode !== Timescale.Day) return
    onClick(date)
  }, [date, mode, onClick])

  const isToday = isSameDay(TODAY, date)
  const dateNumber = getDate(date)

  const dayKey = format(date, 'yyyy-MM-dd')
  const dayLeafBreakdown = breakdown?.breakdownByDate?.[dayKey]
  const dayBarBreakdown = breakdown?.barBreakdownByDate?.[dayKey]
  // Content mode's focused week derives its count from the same breakdown that
  // feeds the tooltip rows, so the header total reconciles with the rows and
  // respects active filters. Only the focused week receives a breakdown; other
  // weeks fall back to the server histogram count.
  const totalCount =
    isContentMode && breakdown
      ? Object.values(dayLeafBreakdown ?? {}).reduce((sum, n) => sum + n, 0)
      : daySize
  const tooltipContent = renderBreakdownTooltipContent({
    breakdown: dayLeafBreakdown,
    colorFor: breakdown?.colorFor,
    heading: formatDayHeading(date),
    isContentMode: Boolean(isContentMode),
    nameFor: breakdown?.nameFor,
    parentOf: breakdown?.parentOf,
    totalCount,
  })

  /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
  return (
    <div
      onClick={onClickDay}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={kuiperClassNames('histogramDay', `mode-${mode}`, {
        isActiveDropTarget,
        isFirstDayOfWeek,
        isForbiddenDropTarget,
        isLastDayOfWeek,
        isNotWithinStory,
        isToday,
      })}
    >
      {isToday && (
        <div className={kuiperClassNames('todayBar')}>
          <div className={kuiperClassNames('todayBarInner')} />
        </div>
      )}

      <div className={kuiperClassNames('histogramDay-metaData')}>
        {dateNumber === 1 && (
          <span className={kuiperClassNames('monthLabel')}>
            <strong>{format(date, 'MMMM')}</strong> {format(date, 'yyyy')}
          </span>
        )}
        <span className={kuiperClassNames('date')}>{dateNumber}</span>
        {/* Bar-only trigger so the week-level tooltip still shows when
            the pointer is anywhere else in the day cell. */}
        <Tooltip
          content={tooltipContent}
          delay={isTooltipFastTrack ? 0 : 100}
          onOpenChange={(open) => onBarHoverChange?.(open)}
          side="bottom"
          sideOffset={12}
          zIndex={999}
        >
          <HistogramBar
            breakdown={dayBarBreakdown}
            colorFor={breakdown?.colorFor}
            height={dayHeight}
            shouldStack={Boolean(breakdown?.shouldStackSegments)}
            totalCount={totalCount}
          />
        </Tooltip>
      </div>

      {isForbiddenMomentDrop && (
        <DropWarner
          date={date}
          isNotWithinStory={isNotWithinStory}
          isSameDateAsDragged={isSameDateAsDragged}
          story={story}
        />
      )}
    </div>
  )
  /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
}

Day.propTypes = {
  breakdown: object,
  date: instanceOf(Date),
  dayHeight: number,
  daySize: number,
  draggedMomentParams: shape({
    id: string.isRequired,
    scheduledAt: oneOfType([instanceOf(Date), string]).isRequired,
    primaryStoryInterval: shape({
      start: instanceOf(Date).isRequired,
      end: instanceOf(Date).isRequired,
    }),
  }),
  isContentMode: bool,
  isFirstDayOfWeek: bool,
  isLastDayOfWeek: bool,
  isTooltipFastTrack: bool,
  mode: oneOf([Timescale.Day, Timescale.Month, Timescale.Week]),
  onBarHoverChange: func,
  onClick: func,
  onRescheduleContent: func,
  onUpdateMoment: func,
  story: object,
}

export default Day
